import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import api from '../../api';
import { formatCurrencyInCents, formatDate, presentUuid } from '../../helpers/formatters';
import {
    formatReplacementPartNames,
    getMaintenanceTypeBadgeProps,
    maintenancePartsTotalCents,
    minutesToDuration,
    partName,
    partValueCents,
} from '../../helpers/printerMaintenances';

function Detail({ label, children }) {
    return (
        <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Box sx={{ mt: 0.25 }}>{children}</Box>
        </Grid>
    );
}

export default function MaintenanceDetailsModal({ open, maintenanceUuid, printerName, onClose }) {
    const [maintenance, setMaintenance] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);

    useEffect(() => {
        if (!open || !maintenanceUuid) {
            return undefined;
        }

        let cancelled = false;
        setIsLoading(true);
        setDidNotLoad(false);
        setMaintenance(null);

        api.get(`printer-maintenances/${maintenanceUuid}`)
            .then((response) => {
                if (!cancelled) {
                    setMaintenance(response.data?.data || null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDidNotLoad(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [open, maintenanceUuid]);

    const parts = maintenance?.replacement_parts || [];
    const partsTotalCents = maintenancePartsTotalCents(parts);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                Manutenção
                {maintenanceUuid ? ` #${presentUuid(maintenanceUuid)}` : ''}
            </DialogTitle>
            <DialogContent dividers>
                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}
                {!isLoading && didNotLoad && (
                    <Typography color="error">
                        Não foi possível carregar os detalhes desta manutenção.
                    </Typography>
                )}
                {!isLoading && !didNotLoad && maintenance && (
                    <Grid container spacing={2}>
                        <Detail label="Data">
                            <Typography>{formatDate(maintenance.occurred_at)}</Typography>
                        </Detail>
                        <Detail label="Impressora">
                            <Typography>{printerName || '—'}</Typography>
                        </Detail>
                        <Detail label="Tipo">
                            <Chip {...getMaintenanceTypeBadgeProps(maintenance.type)} />
                        </Detail>
                        <Detail label="Contagem de horas">
                            <Typography>{maintenance.printer_hours_count || 0}h</Typography>
                        </Detail>
                        <Detail label="Tempo de análise">
                            <Typography>{minutesToDuration(maintenance.analysis_duration_minutes)}</Typography>
                        </Detail>
                        <Detail label="Tempo de correção">
                            <Typography>{minutesToDuration(maintenance.correction_duration_minutes)}</Typography>
                        </Detail>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Descrição do problema
                            </Typography>
                            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                {maintenance.problem_description || '—'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                O que foi feito
                            </Typography>
                            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                {maintenance.performed_actions || '—'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Peças
                            </Typography>
                            {parts.filter((part) => partName(part)).length === 0 ? (
                                <Typography>{formatReplacementPartNames(parts)}</Typography>
                            ) : (
                                parts.filter((part) => partName(part)).map((part, index) => {
                                    const cents = partValueCents(part);
                                    return (
                                        <Typography key={`${partName(part)}-${index}`}>
                                            {partName(part)}
                                            {cents > 0 ? ` · ${formatCurrencyInCents(cents)}` : ''}
                                        </Typography>
                                    );
                                })
                            )}
                            {partsTotalCents > 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Valor gasto: {formatCurrencyInCents(partsTotalCents)}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}
